import { useSyncExternalStore, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getSessionFlags, hasSession, subscribeSessionFlags } from '../../lib/session';
import { TenantAccessDenied } from './TenantAccessDenied';
import { TenantSuspended } from './TenantSuspended';
import { MeRequestError, useMe } from './useMe';

function signInPath(pathname: string, search: string): string {
  const returnTo = `${pathname}${search}`;
  return returnTo === '/' ? '/sign-in' : `/sign-in?return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * Session bootstrap (spec 10 §6, AC-10-026): without a session cookie or after
 * any `401` the app goes to `/sign-in` and comes back afterwards (at `/` it
 * renders `guest` instead, the landing page); without
 * access to the active tenant it shows the full-page error state. While
 * `/v1/me` loads, the shell renders and the account area shows a skeleton.
 */
export function SessionGate({ children, guest }: { children: ReactNode; guest?: ReactNode }) {
  const location = useLocation();
  const me = useMe();
  const flags = useSyncExternalStore(subscribeSessionFlags, getSessionFlags);

  const expired = flags.unauthenticated || (me.error instanceof MeRequestError && me.error.status === 401);
  if (!hasSession() || expired) {
    // At `/` a guest gets the public product page instead of the sign-in redirect (E380).
    if (guest && location.pathname === '/') return guest;
    return <Navigate to={signInPath(location.pathname, location.search)} replace />;
  }
  if (flags.tenantSuspended) return <TenantSuspended />;
  if (flags.tenantDenied || (me.data && me.data.active_tenant === null)) {
    return <TenantAccessDenied />;
  }
  return children;
}
